import {
  Button,
  cn,
  Dropdown,
  DropdownMenu,
  DropdownTrigger,
  Skeleton
} from '@heroui/react';
import { NavArrowDown } from 'iconoir-react';

import {
  renderActionsSection,
  renderOptionsSection
} from './filterSelectMenuSections';
import { FilterSelectSearch } from './FilterSelectSearch';
import type { ContentHeaderFilterSelectProps } from './types';
import { useFilterSelectState } from './useFilterSelectState';

export type {
  ContentHeaderFilterSelectProps,
  ContextModeProps,
  StandaloneModeProps
} from './types';

export function ContentHeaderFilterSelect<T extends string = string>(
  props: ContentHeaderFilterSelectProps<T>
) {
  const {
    label,
    options,
    selectionMode = 'single',
    displayCount = true,
    selectionLimit,
    autoApply = false,
    isSearchable = false,
    className,
    ariaLabel,
    'data-testid': dataTestId
  } = props;

  const state = useFilterSelectState(props, { selectionMode, autoApply });
  const selectedCount = state.localSelectedValues.length;

  const selectedLabel =
    options.find(opt => opt.value === state.localSelectedValues[0])?.label ||
    label ||
    options[0]?.label;

  if (!state.isLoaded && !state.isStandalone) {
    return <Skeleton className='h-[30px] w-20 rounded-md' />;
  }

  if (!options.length) {
    return null;
  }

  return (
    <Dropdown
      onClose={state.handleClose}
      isOpen={state.isDropdownOpen}
      onOpenChange={state.handleOpenChange}
      classNames={{ content: 'p-0 pl-1.5' }}
      className='rounded-md bg-white shadow-xl'
    >
      <DropdownTrigger data-testid={dataTestId}>
        <Button
          variant='bordered'
          className={cn(
            'transition-colors border border-outline text-title-sm py-1 px-2 h-auto min-w-0',
            state.isNonDefaultSelected &&
              'border-[#00000052] bg-surface-container-low !text-label-lg',
            className
          )}
          endContent={<NavArrowDown className='h-4 w-4' />}
        >
          <span className='text-sm'>
            {selectionMode === 'single' ? selectedLabel : label}
          </span>
          {displayCount &&
            selectionMode === 'multiple' &&
            selectedCount > 0 && (
              <span className='flex h-5 min-w-5 items-center justify-center rounded-sm bg-surface-container-low px-1 text-xs leading-tight tracking-wide text-on-surface-variant'>
                {`${selectedCount}`}
              </span>
            )}
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={ariaLabel}
        selectionMode={selectionMode}
        classNames={{ base: 'p-0' }}
        onSelectionChange={state.handleSelectionChange}
        closeOnSelect={selectionMode === 'single'}
        topContent={
          <FilterSelectSearch
            isVisible={isSearchable}
            value={state.searchTerm}
            onChange={state.setSearchTerm}
            filterTestId={dataTestId}
          />
        }
        selectedKeys={
          selectionMode === 'single'
            ? [state.localSelectedValues[0]]
            : state.localSelectedValues
        }
        disabledKeys={state.disabledKeys}
      >
        {renderOptionsSection({
          options: state.visibleOptions,
          selectionMode,
          selectionLimit,
          dataTestId
        })}
        {selectionMode === 'multiple'
          ? renderActionsSection({
              autoApply,
              onClear: state.clearSelection,
              onApply: state.applySelection
            })
          : null}
      </DropdownMenu>
    </Dropdown>
  );
}
